import { IAutoSaveDoc } from './autosave.interface';
import { EventEmitter, Injectable } from '@angular/core';
import { SettingsService } from '../settings/settings.service';
import { ISettings } from 'src/app/core/interfaces';
import { DocumentManagerService } from '../document/document-manager.service';
import { AccountService } from '../auth/account.service';
import { AutoSaveStore } from './autosave-store';
import { IAccountModel } from 'src/app/utils/interfaces';
import { AsgardDocBase } from 'src/app/core/documents/asgard-doc-base';
import { BehaviorSubject, from, Subscription } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { LocalstorageManagerService } from './localstorage-manager.service';
import { ToastrActionEnum } from 'src/app/utils/enums';
import { TranslateService } from '@ngx-translate/core';
import { ToastrDialogService } from '../toastr-dialog.service';
import { ExamDocumentType } from '../exam/enums/document-type.enum';
import { CurrentExamService } from '../exam/current-exam.service';
import { tap, filter } from 'rxjs/operators';
import { TabService } from '../tab.service';

@Injectable({
  providedIn: 'root'
})
export class AutosaveManagerService {
  public autoSaveEnabled: boolean | undefined = undefined;
  // private userId: string | null = null;
  private db: AutoSaveStore | null = null;
  private intervalId: number | null = null;
  private readonly subsciptions: Subscription = new Subscription();

  public unsavedChangesSubj: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public openRestoreDialog: EventEmitter<Partial<IAutoSaveDoc>[]> = new EventEmitter();

  constructor(
    private readonly settingsSvc: SettingsService,
    private readonly docMgrService: DocumentManagerService,
    private readonly account: AccountService,
    private readonly authService: AuthService,
    private readonly localStorageService: LocalstorageManagerService,
    private translate: TranslateService,
    private readonly toastrService: ToastrDialogService,
    private readonly examService: CurrentExamService,
    private readonly tabService: TabService,
  ) {
  }

  public cleanOnDestroy(): void {
    // this.userId = null;
    this.db = null;
    this.intervalId = null;
    this.autoSaveEnabled = undefined;
    this.unsavedChangesSubj.next(false);
    this.subsciptions.unsubscribe();
  }

  public async documentPage(documentHash: string): Promise<number | null> {
    if (this.db === null) {
      return null;
    }
    try {
      return await this.db.documentPage(documentHash);
    } catch (err) {
      this.errorHandler(err);
      return null;
    }
  }

  public async updateReadingPosition(documentHash: string | null, readingItemIdx: number | null, readingItemPageIdx: number | null = null): Promise<void> {
    if (this.db && documentHash) {
      try {
        this.db.updateReadingPosition(documentHash, readingItemIdx, readingItemPageIdx);
      } catch (err) {
        this.errorHandler(err);
      }
    }
  }

  public async updateDocumentPage(documentHash: string | null, pageIdx: number): Promise<void> {
    if (this.db && documentHash) {
      try {
        this.db.updateDocumentPage(documentHash, pageIdx);
      } catch (err) {
        this.errorHandler(err);
      }
    }
  }

  public async isDocumentUnsavedChanges(docId: string): Promise<string | null> {
    const doc = this.docMgrService.getDocument(docId);
    if (!doc || doc && this.docMgrService.isFocusDocument || this.db === null) {
      return null;
    }
    try {
      return await this.db.isUnsavedChanges(doc.docHash);
    } catch (err) {
      this.errorHandler(err);
      return null;
    }
  }

  public async triggerAutoSave(): Promise<void> {
    if (this.db === null) {
      return;
    }
    if (this.docMgrService.currentDoc && !this.docMgrService.isFocusDocument && this.docMgrService.documentReady) {
      const { currentDoc, currentDoc: { documentId } } = this.docMgrService;

      const unsavedChanges = await currentDoc.getDocumentUnsavedChanged();
      // console.log('triggerAutoSave', unsavedChanges);
      if (unsavedChanges) {
        try {
          unsavedChanges.isSTIFile = !!this.tabService.activeTab?.isSTIFile;
          await this.db.updateDocumentChanges(unsavedChanges, this.examService.getDocDocumentType(documentId));
          await this.updateUnsavedSubj();
        } catch (err) {
          this.errorHandler(err);
        }
      }
    }
  }

  private async updateUnsavedSubj(): Promise<void> {
    const unsaved = await this.getUnsavedChanges();
    this.unsavedChangesSubj.next(unsaved.length === 0 ? false : true);
  }

  public async removeUnsavedChangesAndFile(docId: string, removeFile: boolean = true): Promise<void> {
    if (this.db === null) {
      return;
    }
    const doc = this.docMgrService.getDocument(docId);
    if (!doc || doc && this.docMgrService.isFocusDocument) {
      return;
    }
    try {
      await this.db.removeUnsavedChangesAndFile(doc.docHash, removeFile);
      await this.updateUnsavedSubj();
    } catch (err) {
      this.errorHandler(err);
    }
  }

  public async updateOriginalFile(documentHash: string, originalFile: File): Promise<void> {
    if (this.db === null) {
      return;
    }

    let isFileLarger: boolean = false;
    if (originalFile !== null) {
      isFileLarger = await this.localStorageService.isFileLargerThanStorageQuota(originalFile);
    }

    if (isFileLarger) return;

    try {
      await this.db.updateOriginalFile(documentHash, originalFile)
    } catch (err) {
      this.errorHandler(err);
    }
  }

  private async init(): Promise<void> {
    // double check if already create database
    if (this.db !== null) {
      return;
    }
    // check if storage ok
    console.warn('support storage api: ', this.localStorageService.storageCapacityAPI);
    if (!this.localStorageService.storageCapacityAPI) {
      this.showCantGetStorageDialog();
      return;
    }

    this.localStorageService.limitReached$.pipe(
      filter(limitReached => limitReached)
    ).subscribe(() => this.showLimitReachedDialog());

    this.localStorageService.limitReached$.pipe(
      tap(limitReached => console.warn('storage capacity limit reached: ', limitReached)),
      filter(limitReached => !limitReached),
      (() => from(this.account.getAccountInfo())
        .pipe(filter(user => !!user && !!user?.id)))
    ).subscribe((user: IAccountModel) => this.createDB(user.id));
  }

  public addAutosaveSubscriptions(): void {
    this.subsciptions.add(
      this.settingsSvc.getSettingsObservable().subscribe(async (e: ISettings) => {
        let initial = (typeof this.autoSaveEnabled === 'undefined');
        if (e && typeof e.autoSave !== 'undefined' && typeof e.autoSave === 'boolean' && e.autoSave !== this.autoSaveEnabled) {
          this.autoSaveEnabled = e.autoSave;
          console.warn('autosave: ', this.autoSaveEnabled);
          await this.init();
          if (!this.autoSaveEnabled) {
            if (initial) {
              try {
                await this.deleteAllSavedFiles(true);
              } catch (err) {
                this.errorHandler(err);
              }
            }
          } else {
            if (initial) {
              this.getUnsavedChanges().then((e: Partial<IAutoSaveDoc>[]) => {
                if (e && e.length) {
                  this.openRestoreDialog.emit(e);
                }
              });
            }
          }
        }
      })
    );
  }

  public async checkForSavedPageAndReadingPostion(docId: string): Promise<void> {
    const doc = this.docMgrService.getDocument(docId);
    if (!doc) {
      return;
    }
    if (this.db === null) {
      return;
    }
    const savedData = await this.db.getDoc(doc.docHash);
    if (savedData) {
      await doc.lastReadingPositionFounded(savedData.pageIdx, savedData.readingItemIdx,
        savedData.readingItemPageIdx, this.settingsSvc.settings.readingUnit);
    }
  }

  public async getUnsavedChanges(isExam: boolean = false): Promise<IAutoSaveDoc[]> {
    if (this.db === null) {
      return [];
    }
    try {
      return isExam ? await this.db.getUnsavedExamDocuments() : await this.db.getUnsavedDocuments();
    } catch (err) {
      this.errorHandler(err);
      return [];
    }
  }

  public async addOriginalFile(
    documentHash: string | null, originalFile: File | null,
    fileName: string, unsavedChanges: string | null = null, examFileType: ExamDocumentType | null = null): Promise<void> {
    if (this.db === null) {
      return;
    }
    let isFileLarger: boolean = false;
    if (originalFile !== null) {
      isFileLarger = await this.localStorageService.isFileLargerThanStorageQuota(originalFile);
    }

    const isSTIFile = !!this.tabService.activeTab?.isSTI;

    if (originalFile !== null && !isFileLarger) {
      try {
        await this.db.addOriginalFile(
          documentHash,
          originalFile,
          fileName,
          null,
          examFileType,
          isSTIFile
        );
      } catch (err) {
        this.errorHandler(err);
      }
    } else {
      try {
        await this.db.addOriginalFile(
          documentHash,
          null,
          fileName,
          unsavedChanges,
          examFileType,
          isSTIFile
        );
      } catch (err) {
        this.errorHandler(err);
      }
    }
  }

  private createDB(userId: string): void {
    // we need to store reading position besides autosave enabled or disabled
    if (userId && this.authService.isAuthenticated()) {
      this.db = new AutoSaveStore(userId);
    }
  }

  public async deleteDocumentHistory(): Promise<void> {
    if (!this.db) {
      await this.init();
    }

    if (this.db) {
      await this.db.delete();
    }

    return Promise.resolve();
  }

  public async restoreDoc(doc: AsgardDocBase, e: IAutoSaveDoc): Promise<void> {
    try {
      await doc.applyDocumentChanges(e.documentHash, e.unsavedChanges || '');
    } catch (err) {
      this.errorHandler(err);
    }
  }

  public async deleteFiles(files: IAutoSaveDoc[]): Promise<void> {
    const prms: Promise<void>[] = [];
    files.forEach(e => {
      if (this.db) {
        prms.push(this.db.removeUnsavedChangesAndFile(e.documentHash, true));
      }
    });
    try {
      await Promise.all(prms);
    } catch (err) {
      this.errorHandler(err);
    }
  }

  public isDocReady = (docId: string): Promise<AsgardDocBase> => {
    return new Promise((resolve) => {
      const document = this.docMgrService.getDocument(docId);
      if (document && document.isDocumentReady) {
        return resolve(document);
      } else {
        const interval = 100;
        if (this.intervalId) {
          clearInterval(this.intervalId);
          this.intervalId = null;
        }
        this.intervalId = setInterval((() => {
          const doc = this.docMgrService.getDocument(docId);
          if (doc && doc.isDocumentReady) {
            if (this.intervalId) {
              clearInterval(this.intervalId);
              this.intervalId = null;
            }
            return resolve(doc);
          }
        }) as TimerHandler, interval);
      }
    });
  }

  public async addSavedFileAndUpdateReadingPos(file: File, readingItemIdx: number | null,
    readingItemPageIdx: number | null = null): Promise<void> {
    if (file && this.db !== null && this.autoSaveEnabled === true) {
      const isFileLarger = await this.localStorageService.isFileLargerThanStorageQuota(file);
      if (isFileLarger) {
        await this.showLimitReachedDialog();
      } else {
        if (!isFileLarger) {
          try {
            await this.db.addSavedFileAndUpdateReadingPos(file, readingItemIdx, readingItemPageIdx);
          } catch (err) {
            this.errorHandler(err);
          }
        } else {
          this.showLimitReachedDialog();
        }
      }
    }
  }

  private async errorHandler(err: any): Promise<void> {
    console.error(err);
    await this.handleDisablingAutoSave();
    this.toastrService.showToaster(ToastrActionEnum.Error, this.translate.instant('AUTOSAVE_ERRORS.DATABASE_ERROR'));
  }

  private async showLimitReachedDialog(): Promise<void> {
    await this.handleDisablingAutoSave();
    this.toastrService.showToaster(ToastrActionEnum.Error, this.translate.instant('AUTOSAVE_ERRORS.DATABASE_NO_EMPTY_SPACE'));
  }

  private async showCantGetStorageDialog(): Promise<void> {
    await this.handleDisablingAutoSave();
    this.toastrService.showToaster(ToastrActionEnum.Error, this.translate.instant('AUTOSAVE_ERRORS.CANT_GET_STORAGE'));
  }

  private async handleDisablingAutoSave(): Promise<void> {
    this.db = null;
    await this.settingsSvc.saveSettings({ ...this.settingsSvc.settings, autoSave: false });
  }

  public async deleteAllSavedFiles(keepExamFiles: boolean = false): Promise<void> {
    if (this.db === null) {
      return;
    }
    try {
      await this.db.deleteAllSavedFiles(keepExamFiles);
    } catch (err) {
      this.errorHandler(err);
    }
  }

  public async beforeCloseCheckForUnsavedChanges() {
    return new Promise(async (res) => {
      const allIds = this.docMgrService.allDocumentsIDs;
      if (allIds.length !== 0) {
        for (const id of allIds) {
          const unsaved = await this.isDocumentUnsavedChanges(id);
          if (unsaved !== null) {
            return res(true);
          }
        }
        return res(false);
      } else {
        return res(false);
      }
    })
  }

  public async deleteAllExamFiles() {
    if (this.db === null) {
      return;
    }
    try {
      await this.db.deleteAllExamFiles();
    } catch (err) {
      this.errorHandler(err);
    }
  }
}
